import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import { MessageCircle, Send, Loader2 } from 'lucide-react';
import { API, useAuth } from '@/App';

const ContactButton = ({ 
  recipientId, 
  recipientName,
  contextType,  // 'property', 'listing', 'job', 'business', 'event'
  contextId,
  contextTitle,
  className = '',
  variant = 'default',
  size = 'default',
  showText = true
}) => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const getDefaultMessage = () => {
    const messages = {
      property: `Hi! I'm interested in your property "${contextTitle}". Is it still available? I'd like to know more details.`,
      listing: `Hi! I'm interested in "${contextTitle}" that you listed on the marketplace. Is it still available?`,
      job: `Hi! I came across the "${contextTitle}" position and I'm interested in learning more about this opportunity.`,
      business: `Hi! I found your business "${contextTitle}" on ExpatsSphere and wanted to reach out.`,
      event: `Hi! I saw your event "${contextTitle}" and wanted to get more information.`
    };
    return messages[contextType] || `Hi! I wanted to reach out about "${contextTitle}".`;
  };

  const handleOpenChange = (isOpen) => {
    if (!user && isOpen) {
      toast.error('Please sign in to send messages');
      return;
    }
    setOpen(isOpen);
    if (isOpen && !message) {
      setMessage(getDefaultMessage());
    }
  };

  const handleSend = async () => {
    if (!message.trim()) {
      toast.error('Please enter a message');
      return;
    }

    setSending(true);
    try {
      const response = await axios.post(
        `${API}/messages/send-inquiry`,
        {
          recipient_id: recipientId,
          content: message,
          context_type: contextType,
          context_id: contextId,
          context_title: contextTitle
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Message sent successfully!');
      setOpen(false);
      setMessage('');
      
      // Optionally navigate to the conversation
      // navigate(`/messages/${recipientId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleDirectMessage = () => {
    if (!user) {
      toast.error('Please sign in to send messages');
      return;
    }
    navigate(`/messages/${recipientId}`);
  };

  // If no context, just navigate to messages
  if (!contextType) {
    return (
      <Button
        variant={variant}
        size={size}
        onClick={handleDirectMessage}
        className={className}
        data-testid={`contact-${recipientId}`}
      >
        <MessageCircle className={`h-4 w-4 ${showText ? 'mr-2' : ''}`} />
        {showText && 'Message'}
      </Button>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button
          variant={variant}
          size={size}
          className={className}
          data-testid={`contact-${contextType}-${contextId}`}
        >
          <MessageCircle className={`h-4 w-4 ${showText ? 'mr-2' : ''}`} />
          {showText && (contextType === 'property' || contextType === 'listing' ? 'Contact Seller' : 'Send Message')}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5 text-teal-500" />
            Send Message to {recipientName}
          </DialogTitle>
          <DialogDescription>
            Inquiring about: <span className="font-medium text-slate-700">{contextTitle}</span>
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4">
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Write your message..."
            rows={5}
            className="resize-none"
            data-testid="inquiry-message-input"
          />
          <p className="text-xs text-slate-500 mt-2">
            The seller will receive your message and can reply directly.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSend} 
            disabled={sending || !message.trim()}
            data-testid="send-inquiry-button"
          >
            {sending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Send Message
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ContactButton;
